import { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';

import Box from './Box';

import Theme from '../../../styles/Theme/Theme';
import { useTranslationContext } from '../../../contexts/TranslationContext';

type TButtonProps = {
    customColor?: keyof typeof Theme.color;
    severity?: number;
    isActive?: boolean;
};

const DonutButton = styled.button<TButtonProps>`
    position: relative;
    border: 8px solid
        ${({ customColor, theme, isActive, severity }) => {
        if (isActive && severity === 1) {
            return theme.color['warning-4'];
        }
        if (isActive && severity === 2) {
            return theme.color['error-4'];
        }
        if (isActive && severity === 3) {
            return theme.color['error-5'];
        }

        return customColor ? theme.color[customColor] : theme.color['neutral1'];
    }};
    background: white;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    cursor: pointer;

    ${({ isActive, severity, theme }) => {
        if (isActive) {
            return css`
                &::before {
                    content: '';
                    display: block;
                    position: absolute;
                    transform: translate(-50%, -50%);
                    width: 10px;
                    height: 10px;
                    top: 50%;
                    left: 50%;
                    background: ${() => {
                    if (severity === 1) {
                        return theme.color['warning-4'];
                    }
                    if (severity === 2) {
                        return theme.color['error-4'];
                    }
                    if (severity === 3) {
                        return theme.color['error-5'];
                    }
                }};
                    border-radius: 100%;
                }
            `;
        }
    }}
`;

const DottedLine = styled.div`
    flex: 1;
    margin: 20px 6px 0 6px;
    width: 100px;
    height: 1px;
    border-bottom: 1px dashed ${({ theme }) => theme.color['neutral3']};
`;

type TProps = {
    value: number;
    handleSelection: (a: number) => void;
}

const SeveritySelector = ({ value, handleSelection }: TProps) => {
    const [severity, setSeverity] = useState(0);
    const { resources } = useTranslationContext();

    useEffect(() => {
        if (value) {
            setSeverity(value);
        }
    }, [value]);

    let warningText;
    let warningBackground;

    if (severity === 5) {
        warningText = resources.label.severity.warningLow;
        warningBackground = 'warning-4';
    } else if (severity === 4) {
        warningText = resources.label.severity.warningMedium;
        warningBackground = 'error-4';
    } else if (severity === 3) {
        warningText = resources.label.severity.warningHigh;
        warningBackground = 'error-5';
    }

    const handleOnClick = (n: number) => {
        setSeverity(n);
        handleSelection(n);
    }

    return (
        <>
            <Box display="flex" justifyContent="space-between" mb={severity ? 'lg' : undefined}>
                <Box display="flex" flexDirection='column' justifyContent="center" alignItems="center">
                    <DonutButton customColor='neutral0.5' severity={1} onClick={() => handleOnClick(5)} isActive={severity === 5} aria-label="Severity button level 1" />
                    <Box as="span" typography='text-s-bold' mt="xs" mb="xs">1</Box>
                    <Box as="span" typography='ui-xs' customColor="coolgray-10">{resources.label.severity.low}</Box>
                </Box>
                <DottedLine />
                <Box display="flex" flexDirection='column' justifyContent="center" alignItems="center">
                    <DonutButton customColor='neutral3' severity={2} onClick={() => handleOnClick(4)} isActive={severity === 4} aria-label="Severity button level 2" />
                    <Box as="span" typography='text-s-bold' mt="xs" mb="xs">2</Box>
                    <Box as="span" typography='ui-xs' customColor="coolgray-10">{resources.label.severity.medium}</Box>
                </Box>
                <DottedLine />
                <Box display="flex" flexDirection='column' justifyContent="center" alignItems="center">
                    <DonutButton customColor='neutral4' severity={3} onClick={() => handleOnClick(3)} isActive={severity === 3} aria-label="Severity button level 3" />
                    <Box as="span" typography='text-s-bold' mt="xs" mb="xs">3</Box>
                    <Box as="span" typography='ui-xs' customColor="coolgray-10">{resources.label.severity.high}</Box>
                </Box>
            </Box>

            {/* WARNING BOX */}
            {severity ? (
                <Box typography='text-s-regular' customColor="white" p="md" bg={warningBackground as keyof typeof Theme.color} borderRadius="sm">
                    {warningText}
                </Box>
            ) : null}

        </>
    );
};

export default SeveritySelector;
